import { enqueueSnackbar } from 'notistack';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuthContext } from 'src/auth/hooks';
import {
  getCandidateDetails,
  getCandidateDetailsByJobId,
} from 'src/services/candidate.service';
import { getOrganizationId } from 'src/services/user.service';
import PropTypes from 'prop-types';
import { useErrorBoundary } from 'react-error-boundary';

export const CandidateProfileContext = createContext();

export const CandidateProfileProvider = ({
  candidateId,
  jobId,
  candidateType = 'JOB_CANDIDATE',
  children,
  isLoading,
  setIsLoading,
  setShowCandidateProfile,
  needCodingAssessment,
  interactiveInterviewEnabled
}) => {
  // states
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [trigger, setTrigger] = useState(false);

  // hooks
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);
  const { showBoundary } = useErrorBoundary();

  // functions
  const fetchCandidateDetails = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) {
          setIsLoading(true);
        }

        let candidateData = {};

        if (jobId && candidateType === 'JOB_CANDIDATE') {
          const data = await getCandidateDetailsByJobId(
            organization_id,
            jobId,
            candidateId,
          );

          if (data.success) {
            candidateData = data.body;
          } else {
            enqueueSnackbar('Cannot fetch candidate!');
            throw new Error(data.message);
          }
        } else if (candidateType === 'CANDIDATE') {
          const data = await getCandidateDetails(organization_id, candidateId);
          if (data.success) {
            candidateData.candidate_timeline = data.body.candidate_timeline;
            candidateData.candidate_info = data.body;
          } else {
            enqueueSnackbar('Cannot fetch candidate!');
            throw new Error(data.message);
          }
        }

        setCandidateDetails(candidateData);

        if (candidateData?.candidate_info?.profile_json) {
          const profile = JSON.parse(
            candidateData?.candidate_info?.profile_json,
          );
        }
      } catch (err) {
        console.log(err);
        showBoundary(err);
      } finally {
        if (showLoading) {
          setIsLoading(false);
        }
        setShowCandidateProfile(true);
      }
    },
    [
      candidateId,
      candidateType,
      jobId,
      organization_id,
      setIsLoading,
      setShowCandidateProfile,
      showBoundary,
    ],
  );

  // functions
  const refetchWithoutLoading = useCallback(
    () => fetchCandidateDetails(false),
    [fetchCandidateDetails],
  );

  // effects
  useEffect(() => {
    if (candidateId) fetchCandidateDetails();
  }, [fetchCandidateDetails, trigger, candidateId]);

  const contextObj = useMemo(() => {
    const obj = {
      obj: candidateDetails,
      saveChanges: () => {
        setTrigger(prev => !prev);
      },
      isLoading,
      candidateType,
      jobId,
      needCodingAssessment,
      interactiveInterviewEnabled,
      refetchWithoutLoading,
    };
    return obj;
  }, [
    candidateDetails,
    candidateType,
    isLoading,
    jobId,
    needCodingAssessment,
    interactiveInterviewEnabled,
    refetchWithoutLoading,
  ]);

  return (
    <CandidateProfileContext.Provider value={contextObj}>
      {children}
    </CandidateProfileContext.Provider>
  );
};

CandidateProfileProvider.displayName = 'CandidateProfileProvider';

CandidateProfileProvider.propTypes = {
  children: PropTypes.node,
  candidateId: PropTypes.string,
  jobId: PropTypes.string,
  candidateType: PropTypes.string,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  setShowCandidateProfile: PropTypes.func,
  needCodingAssessment: PropTypes.bool,
  interactiveInterviewEnabled: PropTypes.bool,
};
